import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const ELLA_PHOTOS = [
  {
    url: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/tl5ritq5_0b428b53-6add-4c0b-b93b-92d360f06905.JPG",
    alt: "Ella auf dem Dreirad",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/e1joqc8a_2ce88934-44eb-490c-a528-f9343bfa57e6.JPG",
    alt: "Ella am Tisch",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/57q9tq5d_a1afcd51-6c09-4419-b7f1-8deacb19c643.JPG",
    alt: "Ella lachend auf Schaukeltier",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/uv7iwdrd_bf44386e-0f20-49ae-9e26-967a7bda7b45.JPG",
    alt: "Ella lächelt",
  },
];

export default function EllaStory() {
  const [lightbox, setLightbox] = useState(null);
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.1 });
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [textRef, textVisible] = useScrollAnimation({ threshold: 0.15 });
  const [galleryRef, galleryVisible] = useScrollAnimation({ threshold: 0.15 });

  return (
    <section id="ella" data-testid="ella-story-section" className="py-20 md:py-28 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div 
          ref={headerRef}
          className={`text-center mb-12 md:mb-16 animate-fade-up ${headerVisible ? 'visible' : ''}`}
        >
          <p className="font-accent text-xl md:text-2xl mb-2" style={{ color: "#7DD4B5" }}>
            Eine besondere Geschichte
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight" style={{ color: "#1F2937" }}>
            Hallo, ich bin Ella.
          </h2>
          <div className="section-divider mx-auto mt-4" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Text */}
          <div 
            ref={textRef}
            className={`space-y-5 text-left order-2 lg:order-1 animate-fade-left ${textVisible ? 'visible' : ''}`}
          >
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>
              Ich bin 5 Jahre alt und seit meiner Geburt habe ich das Rett-Syndrom.
            </p>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>
              Ich kann nicht laufen. Ich kann meine Hände nicht benutzen.
              Ich kann nicht einmal sagen, was ich gerade fühle.
            </p>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>
              Aber ich bin trotzdem hier – mit einem riesengroßen Herzen und viel Liebe für diese Welt.
            </p>
            <div className="py-4 px-6 rounded-xl" style={{ background: "rgba(149, 225, 187, 0.3)" }}>
              <p className="font-accent text-xl md:text-2xl leading-relaxed" style={{ color: "#b46eb6" }}>
                Mir fehlt nur ein einziges Puzzleteil.
                Und die Forschung weiß inzwischen genau, wo es liegt.
              </p>
            </div>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>
              Vielleicht bin ich heute noch krank – aber wer sagt, dass das für immer so bleiben muss?
            </p>
            <p className="text-base md:text-lg leading-relaxed font-semibold" style={{ color: "#1F2937" }}>
              Es gibt immer eine Lösung.
              Warum sollte es also für mich keine Hoffnung geben?
            </p>
            <div className="pt-4 space-y-2">
              <p className="text-lg md:text-xl font-bold" style={{ color: "#b46eb6" }}>
                Doch dafür brauche ich dich.
              </p>
              <p className="text-base md:text-lg" style={{ color: "#4B5563" }}>
                Du kannst helfen, mein fehlendes Puzzleteil zu finden.
                Du kannst Teil meiner Geschichte sein.
              </p>
            </div>
          </div>

          {/* Photo Gallery - 2x2 Grid with equal sizes */}
          <div 
            ref={galleryRef}
            className={`grid grid-cols-2 gap-3 md:gap-4 order-1 lg:order-2 animate-fade-right ${galleryVisible ? 'visible' : ''}`}
          >
            {ELLA_PHOTOS.map((photo, idx) => (
              <button
                key={idx}
                data-testid={`ella-photo-${idx}`}
                onClick={() => setLightbox(photo)}
                className="gallery-photo overflow-hidden rounded-2xl shadow-md aspect-square"
              >
                <img
                  src={photo.url}
                  alt={photo.alt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-3xl p-2 bg-white rounded-2xl" data-testid="photo-lightbox">
          <DialogTitle className="sr-only">{lightbox?.alt || "Foto von Ella"}</DialogTitle>
          {lightbox && (
            <img
              src={lightbox.url}
              alt={lightbox.alt}
              className="w-full h-auto rounded-xl"
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
